import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import TaskManagement from '../components/tasks/TaskManagement';
import ProjectGanttChart from '../components/gantt/ProjectGanttChart';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  InputNumber, 
  Row, 
  Col, 
  Typography, 
  Divider, 
  Popconfirm, 
  App, 
  Tag, 
  Tabs, 
  Timeline, 
  Statistic, 
  Progress, 
  Spin, 
  Steps, 
  List, 
  Avatar, 
  Alert, 
  message 
} from 'antd';
import {
  ProjectOutlined,
  TeamOutlined,
  CalendarOutlined,
  DollarOutlined,
  ArrowLeftOutlined,
  BarChartOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  UserOutlined,
  CrownOutlined,
  ToolOutlined,
  BulbOutlined,
  EditOutlined,
  SaveOutlined,
  AppstoreOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { MILESTONE_OPTIONS, getMilestoneColor, getMilestoneLabel } from '../constants/milestones';

const { Title, Text } = Typography;

const Projects = () => {
  const { user } = useAuthStore();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectActivities, setProjectActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [projectStats, setProjectStats] = useState({});
  const [teamStats, setTeamStats] = useState(null);
  console.log(selectedProject,"seletedProject");
  

  useEffect(() => {
    fetchProjects();
    fetchEmployees();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      
      // Use PM-specific endpoint for project managers
      if (user && user.role === 'project_manager') {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-manager/projects?managerId=${user.id}`);
        if (response.ok) {
          const result = await response.json();
          const data = result.success ? result.data : result;
          console.log('PM Projects with stats:', data);
          setProjects(data);
        }
      } else {
        // Use general endpoint for admin users
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects`);
        if (response.ok) {
          const result = await response.json();
          const data = result.success ? result.data : result;
          setProjects(data);
        }
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employees`);
      if (response.ok) {
        const result = await response.json();
        // Handle API response structure {success: true, data: [...]}
        const data = result.success ? result.data : result;
        setEmployees(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]); // Set empty array on error
    }
  };

  const fetchProjectActivities = async (projectId) => {
    try {
      setActivitiesLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/timesheets`);
      const data = await response.json();
      
      if (data.success) {
        // Filter activities for the selected project like admin view
        const projectTimesheets = data.data.filter(ts => {
          const tsProjectId = ts.projectId?._id || ts.projectId;
          return tsProjectId === projectId;
        });
        
        console.log('PM View - Project ID:', projectId);
        console.log('PM View - All timesheets:', data.data.length);
        console.log('PM View - Filtered timesheets:', projectTimesheets.length);
        console.log('PM View - Filtered data:', projectTimesheets);
        
        const activities = projectTimesheets.map(ts => {
          // Get employee name from employeeId object or fetch from employees list
          let employeeName = 'Unknown Employee';
          if (ts.employeeId && typeof ts.employeeId === 'object') {
            employeeName = ts.employeeId.name;
          } else if (ts.employeeId) {
            const employee = employees.find(emp => emp._id === ts.employeeId);
            employeeName = employee ? employee.name : 'Unknown Employee';
          }
          
          return {
            title: `${employeeName} submitted timesheet`,
            description: `${ts.hours} hours - ${ts.description || 'No description'}`,
            submittedOn: ts.createdAt || ts.date,
            submittedFor: ts.date,
            status: ts.status,
            hours: ts.hours,
            employeeId: ts.employeeId
          };
        });
        
        setProjectActivities(activities);
        
        // Calculate project stats like admin view
        calculateProjectStats(projectTimesheets);
      }
    } catch (error) {
      console.error('Error fetching project activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const fetchTeamStats = async (projectId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projectId}/team-stats`);
      const data = await response.json();
      
      if (data.success) {
        setTeamStats(data.data);
        console.log('Team Stats:', data.data);
      }
    } catch (error) {
      console.error('Error fetching team stats:', error);
      setTeamStats(null);
    }
  };

  const calculateProjectStats = (timesheets) => {
    if (!selectedProject) return;
    
    // Use the stats from the project object if available (for both PM and admin)
    if (selectedProject.stats) {
      const allocatedHours = selectedProject.totalAllocatedHours || 0;
      const totalHours = selectedProject.stats.totalHours || 0;
      const progressPercentage = allocatedHours > 0 ? Math.round((totalHours / allocatedHours) * 100) : 0;
      const remainingHours = Math.max(0, allocatedHours - totalHours);
      
      console.log('Stats from Project Object:', {
        projectName: selectedProject.name,
        totalHours,
        allocatedHours,
        progressPercentage,
        stats: selectedProject.stats
      });
      
      setProjectStats({
        totalHours,
        totalTimesheets: selectedProject.stats.totalTimesheets || 0,
        approvedTimesheets: selectedProject.stats.approvedTimesheets || 0,
        pendingTimesheets: selectedProject.stats.pendingTimesheets || 0,
        teamSize: selectedProject.stats.teamSize || 0,
        allocatedHours,
        progressPercentage,
        remainingHours
      });
      return;
    }
    
    // Fallback to calculating from timesheets if no stats available
    const totalHours = timesheets.reduce((sum, ts) => sum + ts.hours, 0);
    const totalTimesheets = timesheets.length;
    const approvedTimesheets = timesheets.filter(ts => ts.status === 'approved').length;
    const pendingTimesheets = timesheets.filter(ts => ts.status === 'submitted').length;
    
    // Get unique employees from timesheets
    const uniqueEmployees = new Set();
    timesheets.forEach(ts => {
      if (ts.employeeId) {
        if (typeof ts.employeeId === 'object' && ts.employeeId._id) {
          uniqueEmployees.add(ts.employeeId._id);
        } else if (typeof ts.employeeId === 'string') {
          uniqueEmployees.add(ts.employeeId);
        }
      }
    });
    const teamSize = uniqueEmployees.size;
    
    const allocatedHours = selectedProject.totalAllocatedHours || 0;
    const progressPercentage = allocatedHours > 0 ? Math.round((totalHours / allocatedHours) * 100) : 0;
    const remainingHours = Math.max(0, allocatedHours - totalHours);
    
    setProjectStats({
      totalHours,
      totalTimesheets,
      approvedTimesheets,
      pendingTimesheets,
      teamSize,
      allocatedHours,
      progressPercentage,
      remainingHours
    });
  };

  const updateMilestone = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${selectedProject._id}/milestone`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ milestone: selectedMilestone }),
      });

      if (response.ok) {
        const result = await response.json();
        const updatedProject = result.success ? result.data : result;
        setSelectedProject(updatedProject);
        setProjects(prev => prev.map(p => p._id === updatedProject._id ? updatedProject : p));
        setEditingMilestone(false);
        message.success('Milestone updated successfully');
      } else {
        message.error('Failed to update milestone');
      }
    } catch (error) {
      console.error('Error updating milestone:', error);
      message.error('Failed to update milestone');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'green',
      'on-hold': 'orange',
      'completed': 'blue',
      'cancelled': 'red'
    };
    return colors[status] || 'default';
  };

  const handleProjectClick = (project) => {
    console.log('PM View - Selected Project:', project);
    setSelectedProject(project);
    setSelectedMilestone(project.milestone || 'requirement-gathering');
    
    // Initialize project stats immediately for all users
    const allocatedHours = project.totalAllocatedHours || 0;
    const totalHours = project.stats?.totalHours || 0;
    const progressPercentage = allocatedHours > 0 ? Math.round((totalHours / allocatedHours) * 100) : 0;
    const remainingHours = Math.max(0, allocatedHours - totalHours);
    
    console.log('Overview - Initializing stats:', {
      projectName: project.name,
      totalHours,
      allocatedHours,
      progressPercentage,
      stats: project.stats
    });
    
    setProjectStats({
      totalHours,
      totalTimesheets: project.stats?.totalTimesheets || 0,
      approvedTimesheets: project.stats?.approvedTimesheets || 0,
      pendingTimesheets: project.stats?.pendingTimesheets || 0,
      teamSize: project.stats?.teamSize || 0,
      allocatedHours,
      progressPercentage,
      remainingHours
    });
    
    fetchProjectActivities(project._id);
    fetchTeamStats(project._id);
  };

  const handleBackToList = () => {
    setSelectedProject(null);
    setProjectActivities([]);
    setTeamStats(null);
  };

  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const percentage = record.stats ? 
          Math.round((record.stats.totalHours / (record.totalAllocatedHours || 1)) * 100) : 0;
        return (
          <Progress 
            percent={percentage} 
            size="small" 
            status={percentage > 100 ? 'exception' : 'active'}
          />
        );
      },
    },
    {
      title: 'Team Size',
      key: 'teamSize',
      render: (_, record) => record.stats?.teamSize || 0,
    },
    {
      title: 'Hours',
      key: 'hours',
      render: (_, record) => `${record.stats?.totalHours || 0}/${record.totalAllocatedHours || 0}`,
    },
  ];  
  const renderProjectCards = () => {
    return (
      <Row gutter={[24, 24]}>
        {projects.map((project) => (
          <Col xs={24} sm={12} lg={8} xl={6} key={project._id}>
            <Card
              hoverable
              className="h-full"
              actions={[
                <Button 
                  type="primary" 
                  icon={<ProjectOutlined />}
                  onClick={() => handleProjectClick(project)}
                >
                  View Details
                </Button>
              ]}
            >
              <Card.Meta
                title={
                  <div className="flex items-center justify-between">
                    <span className="truncate">{project.name}</span>
                    <Tag color={getStatusColor(project.status)}>
                      {project.status.toUpperCase()}
                    </Tag>
                  </div>
                }
                description={
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600 truncate">
                      {project.description || 'No description'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag color={getMilestoneColor(project.milestone || 'requirement-gathering')}>
                        {getMilestoneLabel(project.milestone || 'requirement-gathering')}
                      </Tag>
                    </div>
                    <div className="text-xs text-gray-500">
                      Client: {project.client}
                    </div>
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Progress</span>
                        <span>{project.stats ? Math.round((project.stats.totalHours / (project.totalAllocatedHours || 1)) * 100) : 0}%</span>
                      </div>
                      <Progress 
                        percent={project.stats ? Math.round((project.stats.totalHours / (project.totalAllocatedHours || 1)) * 100) : 0}
                        size="small"
                        strokeColor="#52c41a"
                      />
                      <div className="flex justify-between text-xs mt-1">
                        <span>{project.stats?.totalHours || 0}h logged</span>
                        <span>{project.totalAllocatedHours || 0}h allocated</span>
                      </div>
                    </div>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  if (selectedProject) {
    return (
      <div className="p-6">
        <div className="mb-4">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => setSelectedProject(null)}
            className="mb-4"
          >
            Back to Projects
          </Button>
          <div className="flex items-center gap-2">
            <ProjectOutlined className="text-2xl text-blue-500" />
            <Title level={2} className="mb-0">{selectedProject?.name}</Title>
            <Tag color={getStatusColor(selectedProject?.status)} className="ml-2">
              {selectedProject?.status?.charAt(0).toUpperCase() + selectedProject?.status?.slice(1)}
            </Tag>
          </div>
        </div>

        <Row gutter={[24, 0]} className="h-full">
          {/* Main Content - 70% */}
          <Col span={17}>
            <Tabs 
              defaultActiveKey="overview" 
              items={[
                {
                  key: 'overview',
                  label: (
                    <span>
                      <ProjectOutlined />
                      Overview
                    </span>
                  ),
                  children: (
                    <Card>
                      {/* Project Progress Header */}
                      <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                        <Row gutter={[24, 24]} align="middle">
                          <Col xs={24} md={8}>
                            <div className="text-center">
                              <div className="text-4xl font-bold text-blue-600 mb-2">
                                {console.log('Overview Render - projectStats:', projectStats) || (projectStats.progressPercentage || 0)}%
                              </div>
                              <div className="text-lg font-semibold text-gray-700">
                                PROJECT COMPLETION
                              </div>
                              <Progress 
                                percent={projectStats.progressPercentage || 0} 
                                size="small"
                                strokeWidth={8}
                                strokeColor={{
                                  '0%': (projectStats.totalHours || 0) > (projectStats.allocatedHours || 0) ? '#ff4d4f' : '#1890ff',
                                  '100%': (projectStats.totalHours || 0) > (projectStats.allocatedHours || 0) ? '#ff7875' : '#52c41a',
                                }}
                                status={(projectStats.totalHours || 0) > (projectStats.allocatedHours || 0) ? 'exception' : 'active'}
                                className="mt-2"
                              />
                            </div>
                          </Col>
                          <Col xs={24} md={16}>
                            <Row gutter={[16, 16]}>
                              <Col xs={12} sm={8}>
                                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                                  <div className="text-2xl font-bold text-green-600">
                                    {projectStats.totalHours || 0}
                                  </div>
                                  <div className="text-sm font-medium text-gray-600">Hours Logged</div>
                                </div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                                  <div className="text-2xl font-bold text-purple-600">
                                    {projectStats.allocatedHours || 0}
                                  </div>
                                  <div className="text-sm font-medium text-gray-600">Hours Allocated</div>
                                </div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                                  <div className="text-2xl font-bold text-orange-600">
                                    {projectStats.teamSize || 0}
                                  </div>
                                  <div className="text-sm font-medium text-gray-600">Team Members</div>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>

                      <Row gutter={[24, 24]}>
                        <Col span={12}>
                          <Typography.Text strong>Project Manager:</Typography.Text>
                          <div className="mt-1">
                            {selectedProject.projectManager?.length > 0 ? (
                              selectedProject.projectManager
                                .map(pm =>
                                  typeof pm === "object"
                                    ? pm.name
                                    : employees.find(emp => emp._id === pm)?.name || "Unknown"
                                )
                                .join(", ")
                            ) : (
                              "Not assigned"
                            )}
                          </div>
                        </Col>
                        <Col span={12}>
                          <Typography.Text strong>Tech Manager:</Typography.Text>
                          <div className="mt-1">
                             {selectedProject.techManager?.length > 0 ? (
                                selectedProject.techManager
                                  .map(tm =>
                                    typeof tm === "object"
                                      ? tm.name
                                      : employees.find(emp => emp._id === tm)?.name || "Unknown"
                                  )
                                  .join(", ")
                              ) : (
                                "Not assigned"
                              )}
                          </div>
                        </Col>
                        {selectedProject.totalAllocatedHours && (
                          <Col span={12}>
                            <Typography.Text strong>Total Allocated Hours:</Typography.Text>
                            <div className="mt-1">{selectedProject.totalAllocatedHours} hrs</div>
                          </Col>
                        )}
                        <Col span={12}>
                          <Typography.Text strong>Created:</Typography.Text>
                          <div className="mt-1">{dayjs(selectedProject.createdAt).format('MMM DD, YYYY')}</div>
                        </Col>
                      </Row>
                      {selectedProject.description && (
                        <div className="mt-4">
                          <Typography.Text strong>Description:</Typography.Text>
                          <div className="mt-2 p-3 bg-gray-50 rounded">
                            {selectedProject.description}
                          </div>
                        </div>
                      )}

                      {/* Gantt Chart Timeline */}
                      <ProjectGanttChart 
                        projectId={selectedProject._id}
                        projectName={selectedProject.name}
                        canEdit={user?.role === 'admin' || user?.role === 'project_manager'}
                        startDate={selectedProject.startDate}
                        endDate={selectedProject.endDate}
                      />
                    </Card>
                  )
                },
                {
                  key: 'statistics',
                  label: (
                    <span>
                      <BarChartOutlined />
                      Statistics
                    </span>
                  ),
                  children: (
                    <Card>
                      <div className="p-6">
                        {/* Project Progress Overview */}
                        <div className="mb-8">
                          <div className="flex items-center justify-between mb-6">
                            <div>
                              <Title level={3} className="mb-2">Project Progress</Title>
                              <Text type="secondary" className="text-lg">
                                Track your project's completion and budget utilization
                              </Text>
                            </div>
                          </div>
                          
                          <div className="text-center mb-6">
                            <Progress
                              type="circle"
                              percent={selectedProject.stats ? 
                                Math.round((selectedProject.stats.totalHours / (selectedProject.totalAllocatedHours || 1)) * 100) : 0} 
                              size="large"
                              strokeWidth={12}
                              strokeColor={{
                                '0%': (selectedProject.stats?.totalHours || 0) > (selectedProject.totalAllocatedHours || 0) ? '#ff4d4f' : '#1890ff',
                                '100%': (selectedProject.stats?.totalHours || 0) > (selectedProject.totalAllocatedHours || 0) ? '#ff7875' : '#52c41a',
                              }}
                              status={(selectedProject.stats?.totalHours || 0) > (selectedProject.totalAllocatedHours || 0) ? 'exception' : 'active'}
                              className="mb-4"
                            />
                          </div>
                          
                          <Row gutter={[32, 16]} className="text-center">
                            <Col xs={24} sm={8}>
                              <div className="p-4 bg-white rounded-lg shadow-sm border">
                                <div className="text-4xl font-bold text-green-600 mb-2">
                                  {selectedProject.stats?.totalHours || 0}
                                </div>
                                <div className="text-lg font-semibold text-gray-700">
                                  HOURS INVESTED
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  Total logged by team
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={8}>
                              <div className="p-4 bg-white rounded-lg shadow-sm border">
                                <div className="text-4xl font-bold text-purple-600 mb-2">
                                  {selectedProject.totalAllocatedHours || 0}
                                </div>
                                <div className="text-lg font-semibold text-gray-700">
                                  HOURS ALLOCATED
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  Project budget
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={8}>
                              <div className="p-4 bg-white rounded-lg shadow-sm border">
                                <div className={`text-4xl font-bold mb-2 ${
                                  ((selectedProject.totalAllocatedHours || 0) - (selectedProject.stats?.totalHours || 0)) < 0 ? 'text-red-600' : 'text-orange-600'
                                }`}>
                                  {Math.abs((selectedProject.totalAllocatedHours || 0) - (selectedProject.stats?.totalHours || 0))}
                                </div>
                                <div className="text-lg font-semibold text-gray-700">
                                  {((selectedProject.totalAllocatedHours || 0) - (selectedProject.stats?.totalHours || 0)) < 0 ? 'HOURS OVER' : 'HOURS REMAINING'}
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  {((selectedProject.totalAllocatedHours || 0) - (selectedProject.stats?.totalHours || 0)) < 0 ? 'Budget exceeded' : 'Budget left'}
                                </div>
                              </div>
                            </Col>
                          </Row>
                          
                          {/* Status Alerts */}
                          {(projectStats.totalHours || 0) > (projectStats.allocatedHours || 0) && (
                            <div className="mt-6 p-4 bg-red-100 border-2 border-red-300 rounded-lg">
                              <div className="flex items-center justify-center gap-3">
                                <span className="text-2xl">🚨</span>
                                <div>
                                  <div className="text-xl font-bold text-red-700">BUDGET EXCEEDED</div>
                                  <div className="text-red-600">
                                    Project is {(projectStats.totalHours || 0) - (projectStats.allocatedHours || 0)} hours over budget
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {(() => {
                            const progressPercentage = projectStats.progressPercentage || 0;
                            const remainingHours = projectStats.remainingHours || 0;
                            
                            return progressPercentage >= 80 && progressPercentage <= 100 && (
                              <div className="mt-6 p-4 bg-yellow-100 border-2 border-yellow-300 rounded-lg">
                                <div className="flex items-center justify-center gap-3">
                                  <span className="text-2xl">⚡</span>
                                  <div>
                                    <div className="text-xl font-bold text-yellow-700">NEARING COMPLETION</div>
                                    <div className="text-yellow-600">
                                      Only {remainingHours} hours remaining in budget
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        <Row gutter={[16, 16]}>
                          <Col span={12}>
                            <Statistic
                              title="Team Members"
                              value={projectStats.teamSize || 0}
                              prefix={<TeamOutlined />}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title="Total Timesheets"
                              value={projectStats.totalTimesheets || 0}
                              prefix={<FileTextOutlined />}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title="Approval Rate"
                              value={projectStats.totalTimesheets > 0 
                                ? Math.round((projectStats.approvedTimesheets / projectStats.totalTimesheets) * 100)
                                : 0}
                              suffix="%"
                              prefix={<CheckCircleOutlined />}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title="Pending Approvals"
                              value={projectStats.pendingTimesheets || 0}
                              valueStyle={{ color: '#fa8c16' }}
                            />
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  )
                },
                {
                  key: 'hours-breakup-view',
                  label: (
                    <span>
                      <BarChartOutlined />
                      Hours Breakup
                    </span>
                  ),
                  children: (
                    <Card title={
                      <div className="flex justify-between items-center">
                        <span>
                          <BarChartOutlined className="mr-2" />
                          Project Hours Breakdown & Milestones
                        </span>
                        {selectedProject.hoursBreakup && selectedProject.hoursBreakup.length > 0 && (
                          <div className="text-lg font-bold text-blue-600">
                            Total: {selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(1)} hours
                          </div>
                        )}
                      </div>
                    }>
                      {selectedProject.hoursBreakup && selectedProject.hoursBreakup.length > 0 ? (
                        <>
                          {/* Summary Cards */}
                          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                            <Row gutter={[16, 16]} align="middle">
                              <Col xs={24} md={8}>
                                <div className="text-center">
                                  <div className="text-3xl font-bold text-blue-600 mb-1">
                                    {selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(1)}h
                                  </div>
                                  <div className="text-sm font-semibold text-gray-700">
                                    TOTAL PLANNED HOURS
                                  </div>
                                </div>
                              </Col>
                              <Col xs={24} md={8}>
                                <div className="text-center">
                                  <div className="text-3xl font-bold text-green-600 mb-1">
                                    {selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.estimatedHours || 0), 0).toFixed(1)}h
                                  </div>
                                  <div className="text-sm font-semibold text-gray-700">
                                    BASE HOURS
                                  </div>
                                </div>
                              </Col>
                              <Col xs={24} md={8}>
                                <div className="text-center">
                                  <div className="text-3xl font-bold text-orange-600 mb-1">
                                    {(selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0) - 
                                      selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.estimatedHours || 0), 0)).toFixed(1)}h
                                  </div>
                                  <div className="text-sm font-semibold text-gray-700">
                                    BUFFER HOURS
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* Milestones List */}
                          <div className="space-y-4">
                            <Title level={4}>Project Milestones</Title>
                            {selectedProject.hoursBreakup.map((milestone, index) => (
                              <Card key={milestone.id || index} size="small" className="border-l-4 border-l-blue-500">
                                <Row gutter={[16, 16]} align="middle">
                                  <Col xs={24} sm={8}>
                                    <div>
                                      <Text strong className="text-lg">{milestone.title}</Text>
                                      <div className="text-sm text-gray-500 mt-1">
                                        Milestone #{index + 1}
                                      </div>
                                    </div>
                                  </Col>
                                  <Col xs={24} sm={4}>
                                    <div className="text-center">
                                      <div className="text-xl font-bold text-green-600">
                                        {milestone.estimatedHours}h
                                      </div>
                                      <div className="text-xs text-gray-500">Base Hours</div>
                                    </div>
                                  </Col>
                                  <Col xs={24} sm={4}>
                                    <div className="text-center">
                                      <div className="text-xl font-bold text-orange-600">
                                        {milestone.bufferPercentage}%
                                      </div>
                                      <div className="text-xs text-gray-500">Buffer</div>
                                    </div>
                                  </Col>
                                  <Col xs={24} sm={4}>
                                    <div className="text-center">
                                      <div className="text-xl font-bold text-blue-600">
                                        {milestone.totalHours.toFixed(1)}h
                                      </div>
                                      <div className="text-xs text-gray-500">Total Hours</div>
                                    </div>
                                  </Col>
                                  <Col xs={24} sm={4}>
                                    <div className="text-center">
                                      <Progress 
                                        type="circle" 
                                        size={60}
                                        percent={selectedProject.totalAllocatedHours > 0 ? 
                                          Math.round((milestone.totalHours / selectedProject.totalAllocatedHours) * 100) : 0}
                                        format={(percent) => `${percent}%`}
                                        strokeColor="#1890ff"
                                      />
                                      <div className="text-xs text-gray-500 mt-1">of Total</div>
                                    </div>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                          </div>

                          {/* Comparison with Actual Hours */}
                          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                            <Title level={5}>Planning vs Actual Comparison</Title>
                            <Row gutter={[16, 16]}>
                              <Col xs={24} sm={8}>
                                <Statistic
                                  title="Planned Hours"
                                  value={selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(1)}
                                  suffix="h"
                                  valueStyle={{ color: '#1890ff' }}
                                />
                              </Col>
                              <Col xs={24} sm={8}>
                                <Statistic
                                  title="Actual Hours Logged"
                                  value={projectStats.totalHours}
                                  suffix="h"
                                  valueStyle={{ color: '#52c41a' }}
                                />
                              </Col>
                              <Col xs={24} sm={8}>
                                <Statistic
                                  title="Variance"
                                  value={Math.abs(projectStats.totalHours - selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0)).toFixed(1)}
                                  suffix="h"
                                  valueStyle={{ 
                                    color: projectStats.totalHours > selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0) ? '#ff4d4f' : '#52c41a' 
                                  }}
                                  prefix={projectStats.totalHours > selectedProject.hoursBreakup.reduce((sum, item) => sum + (item.totalHours || 0), 0) ? '+' : '-'}
                                />
                              </Col>
                            </Row>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <FileTextOutlined className="text-6xl mb-4" />
                          <div className="text-xl mb-2">No Hours Breakup Available</div>
                          <div className="text-sm">
                            This project was created without milestone planning. 
                            {user?.role === 'admin' ? 'Edit the project to add hours breakup and milestone details.' : 'Contact your admin to add milestone planning details.'}
                          </div>
                        </div>
                      )}
                    </Card>
                  )
                },
                {
                  key: 'team',
                  label: (
                    <span>
                      <TeamOutlined />
                      Team
                    </span>
                  ),
                  children: (
                    <Card>
                      {/* Project Leadership Section */}
                      <div className="mb-6">
                        <Typography.Title level={4}>Project Leadership</Typography.Title>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={12}>
                            <Card size="small" className="bg-blue-50">
                              <div className="flex items-center gap-3">
                                <Avatar icon={<CrownOutlined />} className="bg-blue-500" />
                                <div>
                                  <Typography.Text strong>Project Manager</Typography.Text>
                                  <div className="text-sm">
                                    {selectedProject.projectManager?.length > 0 ? (
                                      selectedProject.projectManager
                                        .map(pm =>
                                          typeof pm === "object"
                                            ? pm.name
                                            : employees.find(emp => emp._id === pm)?.name || "Unknown"
                                        )
                                        .join(", ")
                                    ) : (
                                      "Not assigned"
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(() => {
                                      const pmId = typeof selectedProject.projectManager === 'object' ? 
                                        selectedProject.projectManager._id : selectedProject.projectManager;
                                      const pmTimesheets = projectActivities.filter(activity => 
                                        activity.employeeId === pmId
                                      );
                                      const pmHours = pmTimesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
                                      return `${pmHours} hours contributed`;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Card size="small" className="bg-green-50">
                              <div className="flex items-center gap-3">
                                <Avatar icon={<ToolOutlined />} className="bg-green-500" />
                                <div>
                                  <Typography.Text strong>Tech Manager</Typography.Text>
                                  <div className="text-sm">
                                     {selectedProject.techManager?.length > 0 ? (
                                        selectedProject.techManager
                                          .map(tm =>
                                            typeof tm === "object"
                                              ? tm.name
                                              : employees.find(emp => emp._id === tm)?.name || "Unknown"
                                          )
                                          .join(", ")
                                      ) : (
                                        "Not assigned"
                                      )}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(() => {
                                      const tmId = typeof selectedProject.techManager === 'object' ? 
                                        selectedProject.techManager._id : selectedProject.techManager;
                                      const tmTimesheets = projectActivities.filter(activity => 
                                        activity.employeeId === tmId
                                      );
                                      const tmHours = tmTimesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
                                      return `${tmHours} hours contributed`;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </Col>
                        </Row>
                      </div>

                      {/* Team Members Section */}
                      <div>
                        <Typography.Title level={4}>Team Members</Typography.Title>
                        {teamStats && teamStats.teamMemberStats ? (
                          <div className="space-y-4">
                            {/* Project Summary */}
                            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                              <Row gutter={[16, 16]} align="middle">
                                <Col xs={24} md={8}>
                                  <div className="text-center">
                                    <div className="text-3xl font-bold text-blue-600 mb-1">
                                      {teamStats.totalProjectHours}h
                                    </div>
                                    <div className="text-sm font-semibold text-gray-700">
                                      TOTAL PROJECT HOURS
                                    </div>
                                  </div>
                                </Col>
                                <Col xs={24} md={8}>
                                  <div className="text-center">
                                    <div className="text-3xl font-bold text-green-600 mb-1">
                                      {teamStats.projectInfo.totalAllocatedHours}h
                                    </div>
                                    <div className="text-sm font-semibold text-gray-700">
                                      ALLOCATED HOURS
                                    </div>
                                  </div>
                                </Col>
                                <Col xs={24} md={8}>
                                  <div className="text-center">
                                    <div className="text-3xl font-bold text-purple-600 mb-1">
                                      {teamStats.teamMemberStats.length}
                                    </div>
                                    <div className="text-sm font-semibold text-gray-700">
                                      TEAM MEMBERS
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>

                            {teamStats && teamStats.teamMemberStats && teamStats.teamMemberStats.length > 0 ? (
                              <div className="grid gap-4">
                                {(Array.isArray(teamStats.teamMemberStats) ? teamStats.teamMemberStats : []).map((memberStat, index) => {
                                const getRoleIcon = (type) => {
                                  switch (type) {
                                    case 'project_manager':
                                      return <CrownOutlined className="text-blue-500" />;
                                    case 'tech_manager':
                                      return <ToolOutlined className="text-green-500" />;
                                    default:
                                      return <UserOutlined />;
                                  }
                                };

                                const getRoleColor = (type) => {
                                  switch (type) {
                                    case 'project_manager':
                                      return 'blue';
                                    case 'tech_manager':
                                      return 'green';
                                    default:
                                      return 'blue';
                                  }
                                };

                                const getRoleLabel = (role, type) => {
                                  if (type === 'project_manager') return 'PROJECT MANAGER';
                                  if (type === 'tech_manager') return 'TECH MANAGER';
                                  return role?.replace('_', ' ').toUpperCase() || 'EMPLOYEE';
                                };

                                // 🔑 Normalize employeeId
                                const employeesList = Array.isArray(memberStat.employeeId)
                                  ? memberStat.employeeId
                                  : [memberStat]; // if it's not array, just wrap current memberStat

                                
                                return (
                                 <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
                                    {employeesList.map((emp, empIndex) => (
                                      <div key={emp._id || emp.employeeId || empIndex} className="mb-4 last:mb-0">
                                        {/* Top Row - Name and Role */}
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-3">
                                            <Avatar
                                              size={40}
                                              icon={getRoleIcon(memberStat.type)}
                                              className={`${memberStat.type === 'project_manager' ? 'bg-blue-500' : memberStat.type === 'tech_manager' ? 'bg-green-500' : 'bg-blue-500'}`}
                                            />
                                            <div>
                                              <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-900">
                                                  {emp.name || memberStat.name || "Unknown"}
                                                </span>
                                                <Tag color={getRoleColor(memberStat.type)} className="text-xs px-2 py-0">
                                                  {getRoleLabel(emp.role || memberStat.role, memberStat.type)}
                                                </Tag>
                                              </div>
                                              <div className="text-sm text-gray-500">
                                                {emp.email || memberStat.email || ""}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Bottom Row - Metrics (shared for the role, not per-employee) */}
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-6">
                                            <div className="text-center">
                                              <div className="text-xs text-gray-500 mb-1">Allocation</div>
                                              <div className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                                                {memberStat.allocation}%
                                              </div>
                                            </div>
                                            <div className="text-center">
                                              <div className="text-xs text-gray-500 mb-1">Hours Contributed</div>
                                              <div className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm font-medium">
                                                {memberStat.totalHours} hours
                                              </div>
                                            </div>
                                            <div className="text-center">
                                              <div className="text-xs text-gray-500 mb-1">Timesheets</div>
                                              <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                                                {memberStat.timesheetCount} timesheets
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-center">
                                            <div className="text-xs text-gray-500 mb-1">of Project time</div>
                                            <div className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">
                                              {memberStat.percentage}%
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })}
                              </div>
                            ) : (
                              <Alert message="No team members assigned to this project" type="info" />
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            {teamStats === null ? (
                              <Spin size="large" />
                            ) : (
                              <Alert message="No team members assigned to this project" type="info" />
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  )
                }
              ]}
            />
          </Col>
          
          {/* Right Sidebar with Tabs - 30% */}
          <Col span={7}>
            <Card className="h-full">
              <Tabs
                defaultActiveKey="activities"
                size="small"
                items={[
                  {
                    key: 'activities',
                    label: (
                      <span>
                        <BulbOutlined />
                        Activities
                      </span>
                    ),
                    children: (
                      <div className="max-h-96 overflow-y-auto pr-2">
                        {activitiesLoading ? (
                          <Spin />
                        ) : (
                          <Timeline 
                            size="small"
                            items={projectActivities.length > 0 ? 
                              projectActivities.map((activity, index) => ({
                                key: index,
                                dot: <CheckCircleOutlined className="text-green-500" />,
                                children: (
                                  <div>
                                    <Text strong className="text-sm">{activity.title}</Text>
                                    <br />
                                    <Text type="secondary" className="text-xs">
                                      {activity.description}
                                    </Text>
                                    <br />
                                    <Text type="secondary" className="text-xs">
                                      Submitted On: {dayjs(activity.submittedOn).format('MMM DD, YYYY hh:mm A')}
                                    </Text>
                                    <br />
                                    <Text type="secondary" className="text-xs">
                                      Submitted For: {dayjs(activity.submittedFor).format('MMM DD, YYYY')}
                                    </Text>
                                  </div>
                                )
                              })) : [{
                                key: 'no-activities',
                                dot: <CheckCircleOutlined className="text-green-500" />,
                                children: <Text type="secondary" className="text-sm">No activities recorded yet</Text>
                              }]
                            }
                          />
                        )}
                      </div>
                    )
                  },
                  {
                    key: 'milestones',
                    label: (
                      <span>
                        <ProjectOutlined />
                        Milestones
                      </span>
                    ),
                    children: (
                      <div>
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <Tag 
                              color={getMilestoneColor(selectedProject.milestone || 'requirement-gathering')} 
                              className="px-2 py-1 text-xs font-medium"
                            >
                              {getMilestoneLabel(selectedProject.milestone || 'requirement-gathering')}
                            </Tag>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => setEditingMilestone(true)}
                            >
                              Edit
                            </Button>
                          </div>
                          
                          {editingMilestone && (
                            <div className="mb-3 p-2 bg-gray-50 rounded border">
                              <Select
                                value={selectedMilestone}
                                onChange={setSelectedMilestone}
                                className="w-full mb-2"
                                size="small"
                              >
                                {MILESTONE_OPTIONS.map(milestone => (
                                  <Select.Option key={milestone.key} value={milestone.key}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-2 h-2 rounded-full" 
                                        style={{ backgroundColor: milestone.color }}
                                      />
                                      <span className="text-xs">{milestone.label}</span>
                                    </div>
                                  </Select.Option>
                                ))}
                              </Select>
                              <div className="flex gap-1">
                                <Button 
                                  type="primary" 
                                  size="small"
                                  icon={<SaveOutlined />}
                                  onClick={updateMilestone}
                                >
                                  Save
                                </Button>
                                <Button 
                                  size="small"
                                  onClick={() => {
                                    setEditingMilestone(false);
                                    setSelectedMilestone(selectedProject.milestone || 'requirement-gathering');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          {/* Vertical Steps */}
                          <Steps
                            direction="vertical"
                            current={MILESTONE_OPTIONS.findIndex(m => m.key === (selectedProject.milestone || 'requirement-gathering'))}
                            size="small"
                            items={MILESTONE_OPTIONS.map(milestone => ({
                              title: <span className="text-xs">{milestone.label.split(' ')[0]}</span>,
                              description: milestone.key === (selectedProject.milestone || 'requirement-gathering') ? 
                                <span className="text-xs text-blue-600 font-medium">Current</span> : null
                            }))}
                          />
                        </div>
                      </div>
                    )
                  },
                  {
                    key: 'tasks',
                    label: (
                      <span>
                        <CheckCircleOutlined />
                        Tasks
                      </span>
                    ),
                    children: (
                      <div style={{ height: '600px', overflowY: 'auto' }}>
                        <TaskManagement 
                          currentUser={user} 
                          selectedProjectId={selectedProject._id}
                          compactView={true}
                        />
                      </div>
                    )
                  }
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <ProjectOutlined className="mr-2" />
          Projects
          {user && user.role === 'project_manager' && (
            <Text type="secondary" className="ml-2 text-sm font-normal">
              (Your Assigned Projects)
            </Text>
          )}
        </Title>
        <div className="flex items-center gap-2">
          <Button.Group>
            <Button
              type={viewMode === 'cards' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </Button>
            <Button
              type={viewMode === 'table' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode('table')}
            >
              Table
            </Button>
          </Button.Group>
        </div>
      </div>

      {projects.length === 0 && !loading ? (
        <Card>
          <div className="text-center py-8">
            <ProjectOutlined className="text-4xl text-gray-300 mb-4" />
            <Title level={4} type="secondary">
              {user && user.role === 'project_manager' 
                ? 'No projects assigned to you yet' 
                : 'No projects found'
              }
            </Title>
            <Text type="secondary">
              {user && user.role === 'project_manager' 
                ? 'Contact your admin to get assigned to projects.' 
                : 'Projects will appear here once they are created.'
              }
            </Text>
          </div>
        </Card>
      ) : (
        <Card loading={loading}>
          {viewMode === 'cards' ? renderProjectCards() : (
            <Table
              columns={columns}
              dataSource={projects}
              rowKey="_id"
              loading={loading}
              onRow={(record) => ({
                onClick: () => handleProjectClick(record),
                style: { cursor: 'pointer' },
                className: 'hover:bg-gray-50 transition-colors duration-200'
              })}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `Total ${total} projects`,
              }}
              scroll={{ x: 800 }}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default Projects;
